import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { canManageTeam } from '@/lib/roles';
import { SetupGuide } from './SetupGuide';
import { useSetupProgress } from '../useSetupProgress';

/**
 * Floating setup guide: a pinned pill in the bottom-right corner that opens a
 * popover with the onboarding checklist. Mounted once in the app layout so it
 * floats over the whole panel.
 *
 * Only shows for who can resolve setup (org admins), once a station is selected,
 * and retires itself when everything required is done. Opens on first mount;
 * afterwards the user toggles it from the pill.
 */
export function SetupGuideLauncher() {
  const { t } = useTranslation('onboarding');
  const { lng = 'es' } = useParams();
  const { user } = useAuth();
  const progress = useSetupProgress();
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!canManageTeam(user?.role)) return null;
  // No flash: nothing until we know the real state, nor when it's all done.
  if (progress.isPending || progress.allDone) return null;

  const { requiredDone, requiredTotal } = progress;
  const pct = requiredTotal === 0 ? 0 : Math.round((requiredDone / requiredTotal) * 100);
  // The whole first onboarding funnels toward logging a fuel reception: each step
  // returns to the new-fuel-reception page, whose gate clears once its data is in.
  const returnTo = `/${lng}/fuel-receptions/new`;

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3 max-sm:inset-x-3 max-sm:items-stretch">
      {open ? (
        <section
          role="dialog"
          aria-label={t('setup.title')}
          className="flex max-h-[min(72vh,640px)] w-[360px] flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-surface max-sm:w-auto"
        >
          <header className="relative bg-gradient-to-br from-brand-700 to-brand-500 px-5 pb-4 pt-5 text-white">
            <button
              type="button"
              aria-label={t('setup.close')}
              onClick={() => setOpen(false)}
              className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
            <p className="text-[13px] font-semibold opacity-90">{t('setup.greeting')}</p>
            <h2 className="mb-3.5 mt-0.5 text-xl font-semibold">{t('setup.title')}</h2>
            <div className="flex items-center gap-2.5">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/30">
                <div
                  className="h-full rounded-full bg-white transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="flex-shrink-0 text-xs font-bold">
                {t('setup.progress', { done: requiredDone, total: requiredTotal })}
              </span>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto p-2">
            <SetupGuide progress={progress} returnTo={returnTo} onNavigate={() => setOpen(false)} />
          </div>

          <footer className="border-t border-line px-4 py-2.5 text-center text-[11px] text-muted">
            {t('setup.credit')}
          </footer>
        </section>
      ) : null}

      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-2 self-end rounded-full bg-brand-600 px-4 py-3 text-sm font-bold text-white shadow-surface transition hover:-translate-y-0.5 hover:bg-brand-700"
      >
        <Sparkles className="h-4 w-4" aria-hidden="true" />
        {t('setup.launcher')}
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold">
          {requiredDone}/{requiredTotal}
        </span>
      </button>
    </div>
  );
}
